import type { IntrinsicSystemProps } from '@hover/blueprint';
import {
  Icon,
  forwardRef,
  motion,
  system,
  transition,
  useTheme,
} from '@hover/blueprint';
import { iconChevronDown } from '@hover/icons';
import type { Transition } from 'framer-motion';
import { MotionConfig } from 'framer-motion';
import React from 'react';

import { AnimatedTextWidth } from 'src/shared/components/AnimatedTextWidth';

export interface SelectPaletteButtonProps
  extends IntrinsicSystemProps<'button'> {
  /**
   * Color sampling to display to the left of the button label. If `colors`
   * aren't provided, the button displays a chevron icon next to the label
   */
  colors?: string[];
  /**
   * Palette label displayed when the button is expanded
   */
  label: string;
  /**
   * Collapse the button to hide the label and only display either the color
   * sampling or chevron icon
   */
  isExpanded?: boolean;
  /**
   * The Framer Motion transition configuration for expand/collapse
   */
  expandTransition?: Transition;
}

const MotionIcon = motion(Icon);

/**
 * @note The styles on this button should match those of the
 * `segmentedStyleConfig` for the `SubRegion` tabs
 *
 * @todo(design-system): Migrate this to a design system `Button` variant once
 * the system button supports this style, or at the very least extract the
 * styles shared with the `SubRegions` tabs
 *
 * @see {@link ../SubRegions.tsx}
 */
export const SelectPaletteButton = forwardRef<
  SelectPaletteButtonProps,
  'button'
>(({ label, colors, isExpanded = true, expandTransition, ...props }, ref) => {
  const { space } = useTheme();

  return (
    <system.button
      _active={{ bg: 'neutral.700' }}
      _focusVisible={{ outlineColor: 'neutral.0 !important' }}
      _hover={{ bg: 'neutral.700' }}
      alignItems="center"
      appearance="none"
      aria-label={label}
      bg="neutral.800"
      border="none"
      borderRadius="round"
      color="neutral.0"
      cursor="pointer"
      display="flex"
      flexDirection="row"
      fontFamily="body"
      fontWeight="bodyBold"
      height={400}
      outline="2px solid transparent !important"
      outlineOffset="2px"
      px={400}
      py={0}
      ref={ref}
      {...transition.easing250('background', 'color', 'outline-color')}
      textStyle="body500"
      {...props}
    >
      <MotionConfig transition={expandTransition}>
        {colors && (
          <system.div
            alignItems="center"
            display="flex"
            flexDirection="row"
            gap={100}
            height={100}
            mx="200"
          >
            {colors.map((color, index) => (
              <motion.div
                animate={
                  index === 0
                    ? undefined
                    : { marginLeft: isExpanded ? '0px' : space['-200'] }
                }
                backgroundColor={color}
                borderRadius={200}
                height={100}
                key={color}
                outline="2px solid transparent"
                outlineColor={isExpanded ? 'transparent' : 'neutral.700'}
                sx={transition.easing250('outline-color')}
                width={100}
              />
            ))}
          </system.div>
        )}

        <AnimatedTextWidth contentKey={isExpanded}>
          <system.span ml={colors ? '100' : '200'} mr="200">
            {label}
          </system.span>
        </AnimatedTextWidth>

        {!colors && (
          <MotionIcon
            animate={{ marginInline: isExpanded ? space[100] : 0 }}
            icon={iconChevronDown}
            my={0}
          />
        )}
      </MotionConfig>
    </system.button>
  );
});
